import { useEffect } from 'react';
import { extendBillingInfo } from '@sdk/account/utils';
import getNowDate from '@sdk/utils/getNowDate';
import useAuth from '@sdk/authentication/useAuth';
import { SaveClientFlags } from '@sdk/authentication/types';

import { useNavigation } from '@web/navigation/NavigationProvider';
import { useSocketConnectionContext } from '@web/components/SocketConnectionProvider/SocketConnectionProvider';
import { IncomingCommand } from '@web/components/SocketConnectionProvider/types/command';
import ExpiryWarningModal, {
  Props as ExpiryWarningModalProps,
} from '@web/account/billingWarnings/ExpiryWarningModal/ExpiryWarningModal';
import {
  shouldShowAccountLockedModal,
  shouldShowPaymentFailedModal,
  shouldShowCardExpiredModal,
  shouldShowCardExpiringSoonModal,
  shouldShowCardAuthenticationFailedModal,
} from '@web/account/billingWarnings/ExpiryWarningModals/expiryWarningModals.utils';
import { WarningType } from '@web/account/billingWarnings/ExpiryWarningModal/expiryWarningModal.types';
import { useBillingInfo } from '@web/account/billing/BillingInfoContext';

type Props = {
  saveClientFlags: SaveClientFlags;
};

const ExpiryWarningModals = ({ saveClientFlags }: Props) => {
  const { billingInfo, onBillingInfoUpdated } = useBillingInfo();
  const { currentUser } = useAuth();
  const { navigate } = useNavigation();
  const { subscribe } = useSocketConnectionContext();

  useEffect(
    () =>
      subscribe(IncomingCommand.SUBSCRIPTION_PAYMENT_ACTION_REQUIRED, ({ msg: data }) => {
        onBillingInfoUpdated(extendBillingInfo(data));
      }),
    [subscribe, onBillingInfoUpdated]
  );

  const onClose = (warningType: WarningType, shouldGoToBilling: boolean): void => {
    if (shouldGoToBilling) {
      navigate('account.overview.billing');
    }

    saveClientFlags({
      flags: {
        [`subscriptionExpiryWarning-${warningType}-${billingInfo.creditCard.lastFour}-${billingInfo.paymentFailedDate}-closed`]:
          getNowDate().toISOString(),
      },
    });
  };

  if (!billingInfo) {
    return null;
  }

  const warningModalArgs: ExpiryWarningModalProps = {
    billingInfo,
    onClose,
    warningType: undefined,
  };

  if (shouldShowAccountLockedModal(billingInfo, currentUser)) {
    warningModalArgs.warningType = WarningType.accountLocked;
  }

  if (shouldShowPaymentFailedModal(billingInfo, currentUser)) {
    warningModalArgs.warningType = WarningType.paymentFailed;
  }

  if (shouldShowCardAuthenticationFailedModal(billingInfo, currentUser)) {
    warningModalArgs.warningType = WarningType.cardAuthenticationFailed;
  }

  if (shouldShowCardExpiredModal(billingInfo, currentUser)) {
    warningModalArgs.warningType = WarningType.cardExpired;
  }

  if (shouldShowCardExpiringSoonModal(billingInfo, currentUser)) {
    warningModalArgs.warningType = WarningType.cardExpiringSoon;
  }

  if (warningModalArgs.warningType) {
    return <ExpiryWarningModal {...warningModalArgs} />;
  }

  return null;
};

export default ExpiryWarningModals;
